// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
//
// Gedeelde helper voor persoongegevens-hergebruik (V49-UAT).
// Eén plek voor het overnemen van bekende persoonsgegevens bij selectie van een
// bestaande persoon (partner, kind, vertrouwenspersoon, executeur, voogd, contact).
//
// Kernregels (acceptatiecriteria):
//  - Neem alleen NIET-LEGE bronwaarden over.
//  - Overschrijf bestaande waarden NOOIT met een lege waarde (geen dataverlies).
//  - Overnemen gebeurt alleen bij een bewuste selectie door de gebruiker.
//  - De geboortedatum van de persoon staat los van de eigenaar-geboortedatum
//    die voor toegang/verificatie wordt gebruikt (die loopt nooit via deze helper).

/** Alle persoonsvelden die ergens in de app kunnen voorkomen. Alles optioneel. */
export interface KnownPersonData {
  firstName?:           string
  lastName?:            string
  /** Volledige naam, afgeleid wanneer een doel slechts één naamveld heeft. */
  fullName?:            string
  relationship?:        string
  dob?:                 string
  phone?:               string
  email?:               string
  // Gestructureerd adres (consistent met PartnerData/PersonalData)
  street?:              string
  houseNumber?:         string
  houseNumberAddition?: string
  postalCode?:          string
  city?:                string
  notes?:               string
}

/** True wanneer een waarde echt iets bevat (niet undefined/null/lege/whitespace string). */
export function hasValue(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== ''
}

/**
 * Kopieer bekende persoonsgegevens naar een doelobject volgens een mapping
 * (bronveld → doelveld). Alleen niet-lege bronwaarden worden overgenomen;
 * bestaande doelwaarden worden nooit met leeg overschreven.
 *
 * Retourneert een NIEUW object (immutability), zodat dit veilig in setState past.
 */
export function applyKnownPerson<T extends object>(
  target: T,
  source: KnownPersonData,
  mapping: Partial<Record<keyof KnownPersonData, keyof T>>,
): T {
  const next = { ...target } as Record<string, unknown>
  for (const srcKey of Object.keys(mapping) as (keyof KnownPersonData)[]) {
    const tgtKey = mapping[srcKey]
    if (tgtKey == null) continue
    const val = source[srcKey]
    if (hasValue(val)) {
      next[tgtKey as string] = val
    }
  }
  return next as T
}

/** Stel een nette volledige naam samen uit voor- en achternaam. */
export function composeFullName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(hasValue).join(' ').trim()
}

/** Bron-velden uit een partnerrecord → KnownPersonData (alleen aanwezige velden). */
export function fromPartner(p: {
  name?: string; lastName?: string; dob?: string; phone?: string; email?: string
  relationForm?: string; notes?: string
  partnerStreet?: string; partnerHouseNumber?: string; partnerHouseNumberAddition?: string
  partnerPostalCode?: string; partnerCity?: string
}): KnownPersonData {
  return {
    firstName:           p.name,
    lastName:            p.lastName,
    fullName:            composeFullName(p.name, p.lastName),
    dob:                 p.dob,
    phone:               p.phone,
    email:               p.email,
    notes:               p.notes,
    street:              p.partnerStreet,
    houseNumber:         p.partnerHouseNumber,
    houseNumberAddition: p.partnerHouseNumberAddition,
    postalCode:          p.partnerPostalCode,
    city:                p.partnerCity,
  }
}

/** Bron-velden uit een kindrecord → KnownPersonData. */
export function fromChild(c: {
  name?: string; lastName?: string; dob?: string; relation?: string
}): KnownPersonData {
  return {
    firstName: c.name,
    lastName:  c.lastName,
    fullName:  composeFullName(c.name, c.lastName),
    dob:       c.dob,
    relationship: c.relation,
  }
}
